import { skipToken } from "@reduxjs/toolkit/query";
import type { GradingId } from "@repo/app-types/entities/teammatePerformanceSummary";
import {
  buildCustomerInteractionUrl,
  type CustomerInteractionDeepLink,
} from "@repo/app-types/helpers/customerInteractionUrl";
import type { GradingReferenceWrapperProps } from "@repo/ui/structuredContent/gradingReferenceContext";
import type { FC } from "react";
import { usePlaybookTeammateGradingQuery } from "../../api/entities/performancePlaybook.js";

interface GradingReferenceLinkAdapterProps extends GradingReferenceWrapperProps {
  /**
   * Optional host callback invoked only when a resolved grading link opens.
   * `referenceTarget` is the tab the link actually navigates to, so reporting
   * can never disagree with the destination.
   */
  onGradingOpen?: (
    gradingId: GradingId,
    referenceTarget: CustomerInteractionDeepLink["tab"] | null,
  ) => void;
}

/**
 * Makes structured content grading references interactive in Admin by linking
 * them straight to the referenced grading in a new tab, with the shared
 * renderer's external-link affordance. This mirrors the copilot's
 * quote/reference links, which navigate directly instead of opening a popover.
 * Supplied to the shared renderers via `GradingReferenceProvider` so the
 * extracted UI stays decoupled from Admin's Redux data layer.
 */
export const GradingReferenceLinkAdapter: FC<
  GradingReferenceLinkAdapterProps
> = (props) => {
  const { reference, className, children, onGradingOpen } = props;
  // Newer content references a grading directly by id; legacy coaching content
  // identifies it by teammate + account, which must be resolved to a grading id
  // before we can build the deep link. The query is skipped when the id is
  // already known.
  const byTeammateAccount = usePlaybookTeammateGradingQuery(
    !reference.gradingId && reference.teammateId && reference.accountId
      ? {
          playbookId: reference.playbookId,
          teammateId: reference.teammateId,
          accountId: reference.accountId,
        }
      : skipToken,
  );

  const gradingId = reference.gradingId ?? byTeammateAccount.data?.id;
  if (!gradingId) {
    // Nothing to link to yet (still resolving, or unresolvable) — render the
    // reference as plain styled text.
    return <span className={className}>{children}</span>;
  }

  const deepLink = buildDeepLinkForReference(reference);
  const href = buildCustomerInteractionUrl({
    playbookId: reference.playbookId,
    gradingId,
    target: deepLink,
  });

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
      onClick={() => onGradingOpen?.(gradingId, deepLink?.tab ?? null)}
    >
      {children}
    </a>
  );
};

/**
 * Maps a reference to the grading detail page's deep link. A criterion pins the
 * reference to its assessment row (even without an explicit `assessment`
 * target); a quote deep-links into the transcript at the quoted fragment. When
 * neither applies the reference opens the page's default tab.
 */
function buildDeepLinkForReference(reference: {
  target?: "assessment" | "transcript";
  criterionId?: string;
  quote?: string;
}): CustomerInteractionDeepLink | undefined {
  if (reference.target === "assessment" || reference.criterionId) {
    return { tab: "assessment", criterionId: reference.criterionId };
  }
  if (reference.target === "transcript") {
    return { tab: "transcript", quote: reference.quote };
  }
  return undefined;
}
